import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Spinner, StatusBadge, KV, ProgressBar } from "@/components/Atoms";
import { JobActions } from "./JobsPage";
import { fmtDate } from "@/lib/format";

const LEVEL_COLOR = {
    info: "text-zinc-300",
    debug: "text-zinc-500",
    warn: "text-cm-warning",
    error: "text-cm-danger",
};

export default function JobDetailPage() {
    const { id } = useParams();
    const [job, setJob] = useState(null);
    const [logs, setLogs] = useState([]);
    const [streamState, setStreamState] = useState("connecting");
    const logsBoxRef = useRef(null);

    const refetch = useCallback(async () => {
        try {
            const { data } = await api.get(`/api/admin/jobs/${id}`);
            setJob(data.job);
            setLogs(data.logs || []);
        } catch {
            /* ignore */
        }
    }, [id]);

    // Initial fetch + open SSE
    useEffect(() => {
        let cancelled = false;
        let es;

        (async () => {
            try {
                const { data } = await api.get(`/api/admin/jobs/${id}`);
                if (cancelled) return;
                setJob(data.job);
                setLogs(data.logs || []);
            } catch (e) {
                setStreamState("error");
                return;
            }

            const base = process.env.REACT_APP_BACKEND_URL || "";
            const url = `${base}/api/admin/jobs/${id}/stream`;
            es = new EventSource(url, { withCredentials: true });
            setStreamState("live");

            es.addEventListener("log", (e) => {
                try {
                    const entry = JSON.parse(e.data);
                    setLogs((prev) => {
                        // Dedup by ts+message
                        if (prev.some((p) => p.ts === entry.ts && p.message === entry.message)) {
                            return prev;
                        }
                        return [...prev, { id: `${entry.ts}-${prev.length}`, ...entry }];
                    });
                } catch {
                    /* malformed event payload — ignore */
                }
            });
            es.addEventListener("status", (e) => {
                try {
                    const s = JSON.parse(e.data);
                    setJob((prev) => (prev ? { ...prev, ...s } : prev));
                } catch {
                    /* malformed status payload — ignore */
                }
            });
            es.addEventListener("end", () => {
                setStreamState("closed");
                es && es.close();
            });
            es.onerror = () => {
                setStreamState("closed");
            };
        })();

        return () => {
            cancelled = true;
            if (es) es.close();
        };
    }, [id]);

    // Auto-scroll logs to bottom on new entries
    useEffect(() => {
        const el = logsBoxRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [logs.length]);

    if (!job) return <div className="p-8"><Spinner /></div>;

    return (
        <div data-testid="job-detail-page">
            <PageHeader
                testid="job-detail-header"
                back="/jobs"
                backLabel="Jobs"
                title={`Job ${job.id.slice(0, 8)}…`}
                subtitle={
                    <span className="font-mono text-xs">
                        {job.type} · cloud {job.cloud_id?.slice(0, 8) || "—"}
                    </span>
                }
                action={
                    <div className="flex items-center gap-2">
                        <JobActions job={job} onDone={refetch} size="lg" />
                        <span
                            className={`cm-pulse-dot ${
                                streamState === "live" ? "bg-cm-success" : streamState === "closed" ? "bg-zinc-600" : "bg-cm-warning"
                            }`}
                        />
                        <span className="cm-label" data-testid="job-stream-state">
                            stream {streamState}
                        </span>
                        <StatusBadge status={job.status} />
                    </div>
                }
            />

            <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="cm-card p-5">
                    <div className="cm-h3 mb-3">Job</div>
                    <KV k="ID" mono v={job.id} />
                    <KV k="Type" mono v={job.type} />
                    <KV
                        k="Cloud"
                        v={
                            job.cloud_id ? (
                                <Link className="text-cm-brand font-mono" to={`/clouds/${job.cloud_id}`}>
                                    {job.cloud_id.slice(0, 8)}…
                                </Link>
                            ) : (
                                "—"
                            )
                        }
                    />
                    <KV k="Status" v={<StatusBadge status={job.status} />} />
                    <KV
                        k="Progress"
                        v={<ProgressBar value={job.progress} className="w-20" />}
                    />
                    <KV k="Step" mono v={job.current_step || "—"} />
                    {job.agent_progress && job.agent_progress.total > 0 && (
                        <KV
                            k="VMs applied"
                            v={
                                <span className="font-mono" data-testid="job-agent-progress">
                                    <span
                                        className={
                                            job.agent_progress.applied >= job.agent_progress.total
                                                ? "text-cm-success"
                                                : "text-cm-warning"
                                        }
                                    >
                                        {job.agent_progress.applied}/{job.agent_progress.total}
                                    </span>{" "}
                                    @ config v{job.agent_progress.target}
                                    {(job.agent_progress.laggards || []).length > 0 && (
                                        <span className="text-zinc-500">
                                            {" · waiting: "}
                                            {(job.agent_progress.laggards || [])
                                                .map((l) => `${l.role}#${l.vmid ?? "?"}`)
                                                .join(", ")}
                                        </span>
                                    )}
                                </span>
                            }
                        />
                    )}
                    <KV k="Started" v={fmtDate(job.started_at)} />
                    <KV k="Finished" v={fmtDate(job.finished_at)} />
                    <KV k="Actor" mono v={job.actor_type ? `${job.actor_type}/${(job.actor_id || "").slice(0, 8)}` : "system"} />
                    {job.error && (
                        <div className="mt-3 p-2 bg-cm-danger/5 border border-cm-danger/40 text-xs text-cm-danger font-mono">
                            {job.error}
                        </div>
                    )}
                </div>

                <div className="lg:col-span-2 cm-card p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div className="cm-h3">Logs ({logs.length})</div>
                        <span className="cm-label">live stream via SSE</span>
                    </div>
                    <div
                        ref={logsBoxRef}
                        className="bg-cm-bg border border-cm-line p-3 h-[60vh] overflow-y-auto font-mono text-xs"
                        data-testid="job-detail-logs"
                    >
                        {logs.map((l) => (
                            <div
                                key={l.id}
                                className={`flex gap-3 border-b border-cm-line/60 last:border-b-0 py-1 ${LEVEL_COLOR[l.level] || ""}`}
                            >
                                <span className="text-zinc-600 shrink-0">{fmtDate(l.ts)}</span>
                                <span className="text-zinc-500 shrink-0 uppercase">{l.level}</span>
                                <span className="text-zinc-500 shrink-0">{l.step || "—"}</span>
                                <span>{l.message}</span>
                            </div>
                        ))}
                        {!logs.length && (
                            <div className="text-zinc-500">No logs yet…</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
